import { useCallback, useEffect, useState } from 'react';
import { Check, Copy, FilePlus2, FolderOpen, Trash2, Upload, X } from 'lucide-react';
import type { DialectDescriptor, ProjectSummary } from '@bits2schema/shared';
import { api } from '../api/client';
import { useI18n } from '../i18n';
import { describeError, formatDateTime } from '../utils/format';
import { Modal } from './Modal';

interface ProjectListPageProps {
  dialects: DialectDescriptor[];
  onOpenProject: (projectId: string) => void;
  onNewImport: () => void;
  onNewGreenfield: () => void;
  /** Změní se po každé akci, která mohla seznam ovlivnit. */
  refreshToken: number;
}

/** E1-US6 — přehled projektů; E1-US9 — smazání projektu s potvrzením. */
export function ProjectListPage({
  dialects,
  onOpenProject,
  onNewImport,
  onNewGreenfield,
  refreshToken,
}: ProjectListPageProps) {
  const { t, language } = useI18n();
  const [projects, setProjects] = useState<ProjectSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pendingDelete, setPendingDelete] = useState<ProjectSummary | null>(null);
  // Epic 15, E15-US3 — kopie projektu; dostupné komukoli s přístupem (creator i editor),
  // kopie je vždy nezávislý projekt (bez historie originálu, bez připojení k DB).
  const [pendingDuplicate, setPendingDuplicate] = useState<ProjectSummary | null>(null);
  const [duplicateName, setDuplicateName] = useState('');
  const [duplicateBusy, setDuplicateBusy] = useState(false);
  const [duplicateError, setDuplicateError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const { projects: list } = await api.listProjects();
      setProjects(list);
      setError(null);
    } catch (caught) {
      setError(describeError(caught, t));
      setProjects([]);
    }
  }, [t]);

  useEffect(() => {
    void load();
  }, [load, refreshToken]);

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    try {
      await api.deleteProject(pendingDelete.id);
      setPendingDelete(null);
      await load();
    } catch (caught) {
      setError(describeError(caught, t));
      setPendingDelete(null);
    }
  };

  const dialectLabel = (project: ProjectSummary) =>
    dialects.find((item) => item.dialectId === project.dialect)?.label ?? project.dialect;

  const openDuplicate = (project: ProjectSummary) => {
    setDuplicateName(t('projects.duplicate.defaultName', { name: project.name }));
    setDuplicateError(null);
    setPendingDuplicate(project);
  };

  const confirmDuplicate = async () => {
    if (!pendingDuplicate) return;
    const trimmed = duplicateName.trim();
    if (!trimmed) {
      setDuplicateError(t('schema.validation.emptyName'));
      return;
    }
    setDuplicateBusy(true);
    setDuplicateError(null);
    try {
      await api.duplicateProject(pendingDuplicate.id, trimmed);
      setPendingDuplicate(null);
      await load();
    } catch (caught) {
      setDuplicateError(describeError(caught, t));
    } finally {
      setDuplicateBusy(false);
    }
  };

  return (
    <section>
      <div className="toolbar">
        <h1>{t('projects.title')}</h1>
        <div className="toolbar__actions">
          <button type="button" onClick={onNewGreenfield}>
            <FilePlus2 size={16} />
            {t('projects.newEmpty')}
          </button>
          <button type="button" className="primary" onClick={onNewImport}>
            <Upload size={16} />
            {t('projects.newFromDatabase')}
          </button>
        </div>
      </div>

      {error && <div className="notice notice--error">{error}</div>}

      <div className="card">
        {projects === null ? (
          <p className="hint">{t('common.loading')}</p>
        ) : projects.length === 0 ? (
          <p className="hint">{t('projects.empty')}</p>
        ) : (
          <table>
            <thead>
              <tr>
                <th>{t('projects.column.name')}</th>
                <th>{t('projects.column.dialect')}</th>
                <th>{t('projects.column.origin')}</th>
                <th className="numeric">{t('projects.column.tables')}</th>
                <th>{t('projects.column.lastImport')}</th>
                <th>{t('projects.column.lastOpened')}</th>
                <th>{t('projects.column.owner')}</th>
                <th>{t('projects.column.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {projects.map((project) => (
                <tr key={project.id}>
                  <td>{project.name}</td>
                  <td>{dialectLabel(project)}</td>
                  <td>
                    <span className="badge">
                      {project.origin === 'import'
                        ? t('projects.origin.import')
                        : t('projects.origin.greenfield')}
                    </span>
                  </td>
                  <td className="numeric">{project.tableCount}</td>
                  <td>{formatDateTime(project.lastImportAt, t, language)}</td>
                  <td>{formatDateTime(project.lastOpenedAt, t, language)}</td>
                  <td>
                    {project.creatorDisplayName}{' '}
                    <span className="badge">{t(`project.role.${project.role}`)}</span>
                  </td>
                  <td>
                    <div className="toolbar__actions">
                      <button type="button" onClick={() => onOpenProject(project.id)}>
                        <FolderOpen size={16} />
                        {t('common.open')}
                      </button>
                      <button type="button" onClick={() => openDuplicate(project)}>
                        <Copy size={16} />
                        {t('projects.duplicate.button')}
                      </button>
                      {/* E14-US9 — mazání je vyhrazené creatorovi; editor tuto akci vůbec nevidí. */}
                      {project.role === 'creator' && (
                        <button
                          type="button"
                          className="danger"
                          onClick={() => setPendingDelete(project)}
                        >
                          <Trash2 size={16} />
                          {t('common.delete')}
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {pendingDelete && (
        <Modal
          title={t('projects.delete.title')}
          onClose={() => setPendingDelete(null)}
          actions={
            <>
              <button type="button" onClick={() => setPendingDelete(null)}>
                <X size={16} />
                {t('common.cancel')}
              </button>
              <button type="button" className="danger" onClick={() => void confirmDelete()}>
                <Trash2 size={16} />
                {t('common.delete')}
              </button>
            </>
          }
        >
          <div className="stack">
            <p>{t('projects.delete.question', { name: pendingDelete.name })}</p>
            <p className="hint">{t('projects.delete.irreversible')}</p>
            <p className="hint">{t('projects.delete.noDbImpact')}</p>
          </div>
        </Modal>
      )}

      {pendingDuplicate && (
        <Modal
          title={t('projects.duplicate.title')}
          onClose={() => setPendingDuplicate(null)}
          actions={
            <>
              <button type="button" onClick={() => setPendingDuplicate(null)}>
                <X size={16} />
                {t('common.cancel')}
              </button>
              <button type="button" className="primary" disabled={duplicateBusy} onClick={() => void confirmDuplicate()}>
                <Check size={16} />
                {t('projects.duplicate.confirm')}
              </button>
            </>
          }
        >
          <div className="stack">
            <div className="field">
              <label htmlFor="project-duplicate-name">{t('projects.duplicate.nameLabel')}</label>
              <input
                id="project-duplicate-name"
                value={duplicateName}
                onChange={(event) => setDuplicateName(event.target.value)}
              />
            </div>
            {duplicateError && <div className="notice notice--error">{duplicateError}</div>}
            <p className="hint">{t('projects.duplicate.hint')}</p>
          </div>
        </Modal>
      )}
    </section>
  );
}
